import React from 'react';
import { Check } from 'lucide-react';

/**
 * Valet av variant på produktsidan, samma för vanliga produkter och paket.
 * Rubriken visar det valda alternativet ("Färg: Pastel Pink"). Är valet en
 * färg visas en färgprick i varje knapp, när färgen går att känna igen på
 * namnet.
 */

const COLOR_WORDS: [RegExp, string][] = [
    [/\b(ivory|elfenben|cream|creme|kräm)\b/i, '#F1EBDD'],
    [/\b(white|vit|vitt)\b/i, '#FAFAF7'],
    [/\b(pink|rosa|blush)\b/i, '#E8B9C0'],
    [/\b(black|svart|graphite|grafit|carbon)\b/i, '#2A2A2A'],
    [/\b(grey|gray|grå|silver)\b/i, '#A3A3A3'],
    [/\b(navy|marin)\b/i, '#25324A'],
    [/\b(blue|blå)\b/i, '#5578A8'],
    [/\b(green|grön|olive|oliv|sage)\b/i, '#6B7B5E'],
    [/\b(red|röd|burgundy|vinröd)\b/i, '#A8393A'],
    [/\b(beige|sand|latte)\b/i, '#D9C4A5'],
    [/\b(brown|brun|mocha|mocka|espresso)\b/i, '#6E4B36'],
    [/\b(yellow|gul)\b/i, '#E5C14B'],
    [/\b(purple|lila|lavender|lavendel)\b/i, '#9C86B8'],
    [/\b(orange)\b/i, '#E07B39'],
];

export const isColorOption = (key: string) => /^(färg|farg|color|colour)$/i.test(key.trim());

/** Färgen för ett alternativ som "Pastel Pink", eller null om namnet inte säger det. */
export const swatchFor = (value: string): string | null => {
    for (const [re, hex] of COLOR_WORDS) if (re.test(value)) return hex;
    return null;
};

interface Props {
    /** Valet, t.ex. "Färg" eller "Malning". */
    name: string;
    /** Rubriken om den ska säga mer, t.ex. "Färg på Flaskan". */
    label?: string;
    values: string[];
    selected?: string;
    onSelect: (value: string) => void;
    accent: string;
}

export const VariantPicker: React.FC<Props> = ({ name, label, values, selected, onSelect, accent }) => {
    const color = isColorOption(name);
    return (
        <fieldset className="mt-6 min-w-0">
            <legend className="text-sm text-stone-600 mb-2">
                <span className="font-bold text-stone-900">{label || name}</span>
                {selected && <>: <span className="font-semibold text-stone-900">{selected}</span></>}
            </legend>
            <div className="flex flex-wrap gap-2">
                {values.map(val => {
                    const on = selected === val;
                    const swatch = color ? swatchFor(val) : null;
                    return (
                        <button
                            key={val}
                            type="button"
                            aria-pressed={on}
                            onClick={() => onSelect(val)}
                            className={`min-h-[44px] inline-flex items-center gap-2 rounded-xl border-2 bg-white text-sm font-semibold text-stone-800 transition-colors ${swatch ? 'pl-2 pr-4' : 'px-4'} ${on ? '' : 'border-stone-200 hover:border-stone-400'}`}
                            style={on ? { borderColor: accent } : undefined}
                        >
                            {swatch && (
                                <span className="relative w-7 h-7 rounded-full flex-shrink-0 border border-black/10" style={{ backgroundColor: swatch }} aria-hidden="true">
                                    {on && <Check size={14} strokeWidth={3} className="absolute inset-0 m-auto" style={{ color: luminance(swatch) > 0.6 ? '#1C1917' : '#fff' }} />}
                                </span>
                            )}
                            {val}
                            {on && !swatch && <Check size={15} strokeWidth={3} aria-hidden="true" style={{ color: accent }} />}
                        </button>
                    );
                })}
            </div>
        </fieldset>
    );
};

const luminance = (hex: string) => {
    const n = parseInt(hex.slice(1), 16);
    return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
};
